/**
 * Listen Prompt Renderer
 *
 * Builds the default permission prompt element: a compact button that recovers
 * speech blocked by an autoplay gate.
 */

/** Muted-speaker glyph, inlined so the prompt renders without a network fetch. */
const MUTED_SPEAKER_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true">' +
  '<path d="M4.2 9.6C4.2 8.9 4.8 8.3 5.5 8.3H8.1C8.4 8.3 8.7 8.2 8.9 8L13.2 4.9C13.9 4.4 14.9 4.9 14.9 5.8V18.2C14.9 19.1 13.9 19.6 13.2 19.1L8.9 16C8.7 15.8 8.4 15.7 8.1 15.7H5.5C4.8 15.7 4.2 15.1 4.2 14.4Z" fill="currentColor"></path>' +
  '<path d="M18 9.3l3.8 3.8M21.8 9.3 18 13.1" stroke="currentColor" stroke-width="2.05" stroke-linecap="round"></path>' +
  '</svg>';

/** Default visible label. Rendered uppercase by CSS. */
export const DEFAULT_LISTEN_LABEL = 'Tap to Listen';

/** Default accessible label and tooltip. */
export const DEFAULT_LISTEN_TOOLTIP = 'Tap to listen to voice playback';

/**
 * Resolve the visible label from the public options.
 *
 * @param options - `label`, falling back to the deprecated `allowLabel`
 * @returns The label, or {@link DEFAULT_LISTEN_LABEL}
 */
export function resolveListenLabel(options: { label?: string; allowLabel?: string }): string {
  return options.label ?? options.allowLabel ?? DEFAULT_LISTEN_LABEL;
}

/** Render inputs for {@link renderListenPrompt}. */
export interface ListenPromptRenderOptions {
  /** Visible button label. */
  label?: string;
  /** Accessible label and tooltip. */
  tooltip?: string;
  /** CSS class prefix. */
  prefix?: string;
  /** Anchored edge. */
  position?: 'top' | 'bottom';
  /** Offset from the anchored edge, px. */
  offset?: number;
  /** Offset from the anchored edge on narrow screens, px. */
  mobileOffset?: number;
}

/**
 * Build the prompt button.
 *
 * @param options - Render inputs
 * @param onActivate - Invoked on click; carries the user activation
 * @returns The button element, not yet attached
 */
export function renderListenPrompt(
  options: ListenPromptRenderOptions,
  onActivate: () => void
): HTMLButtonElement {
  const prefix = options.prefix ?? 'rv';
  const label = options.label ?? DEFAULT_LISTEN_LABEL;
  const tooltip = options.tooltip ?? DEFAULT_LISTEN_TOOLTIP;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = `${prefix}ListenPrompt`;
  button.setAttribute('aria-label', tooltip);
  button.title = tooltip;
  button.setAttribute('data-rv-listen-prompt', '');
  button.setAttribute('data-rv-listen-edge', options.position === 'top' ? 'top' : 'bottom');

  if (options.offset != null) {
    button.style.setProperty(`--${prefix}-listen-offset`, `${options.offset}px`);
  }
  if (options.mobileOffset != null) {
    button.style.setProperty(`--${prefix}-listen-offset-mobile`, `${options.mobileOffset}px`);
  }

  const icon = document.createElement('span');
  icon.className = `${prefix}ListenPromptIcon`;
  icon.innerHTML = MUTED_SPEAKER_SVG; // static glyph markup, no user input

  const text = document.createElement('span');
  text.className = `${prefix}ListenPromptText`;
  text.textContent = label;

  button.append(icon, text);
  button.addEventListener('click', onActivate);

  return button;
}
